import { ChevronDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { type ReactNode, useId, useState } from "react";

import Headline from "~/components/Headline/Headline";
import LoadingSpinner from "~/components/LoadingSpinners/LoadingSpinner";

type Props = {
	title: string;
	/** The key of the open state in `localStorage`. */
	storageKey: string;
	count: number;
	/** While true, a spinner of the pill size takes the place of the count. */
	isLoading?: boolean;
	/** From `md`, the fold line is gone and the content always shows. The line is a button, not a heading. */
	isPhoneOnly?: boolean;
	children: ReactNode;
};

function readIsOpen(storageKey: string) {
	try {
		return localStorage.getItem(storageKey) !== "false";
	} catch {
		return true;
	}
}

function writeIsOpen(storageKey: string, isOpen: boolean) {
	try {
		localStorage.setItem(storageKey, String(isOpen));
	} catch {
		// The state is a convenience, so a blocked storage only loses it.
	}
}

/**
 * A dashboard section with a fold button and a count pill. Each section keeps its own open state under
 * its own key, so the user folds each section on its own.
 */
export function FoldSection(props: Props) {
	const { title, storageKey, count, isLoading = false, isPhoneOnly = false, children } = props;
	const [isOpen, setIsOpen] = useState(() => readIsOpen(storageKey));
	const contentId = useId();
	const titleId = useId();

	const foldButton = (
		<button type="button"
				className="flex min-h-7 w-full items-center gap-2 text-left"
				aria-expanded={isOpen}
				aria-controls={contentId}
				onClick={() => {
					const next = !isOpen;
					writeIsOpen(storageKey, next);
					setIsOpen(next);
				}}>
			{title}
			{isLoading && (
				<span className="flex size-5 shrink-0 items-center justify-center">
					<LoadingSpinner size="sm" theme="neutral" />
				</span>
			)}
			{!isLoading && count > 0 && (
				<span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-700 text-xs font-semibold text-white">
					{count}
				</span>
			)}
			<ChevronDownIcon className={clsx("ml-auto size-5 shrink-0 text-gray-500", !isOpen && "-rotate-90")} />
		</button>
	);

	return (
		// The phone-only line is hidden from `md`, so it cannot name the section there.
		<section aria-label={isPhoneOnly ? title : undefined}
				 aria-labelledby={isPhoneOnly ? undefined : titleId}>
			{isPhoneOnly ? (
				<div className={clsx("text-lg font-semibold text-primary-500 md:hidden", isOpen && "mb-3")}>
					{foldButton}
				</div>
			) : (
				<Headline type="h3"
						  size="lg"
						  weight="semibold"
						  id={titleId}
						  className={isOpen ? "mb-3" : undefined}>
					{foldButton}
				</Headline>
			)}
			<div id={contentId}
				 hidden={!isOpen}
				 className={isPhoneOnly ? "md:block" : undefined}>
				{children}
			</div>
		</section>
	);
}
